import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Banner, Button, Card, EmptyState, Mono, PageBody, PageHeader, StackedList } from "../ui/Field";

type P = { id: string; slug: string; title: string; updated_at: number };
type E = { id: string; slug: string; collection_id: string; name: string; updated_at: number };

export default function Review() {
  const { slug } = useParams();
  const [pages, setPages] = useState<P[]>([]);
  const [entries, setEntries] = useState<E[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api.get<{ pages: P[]; entries: E[] }>(`/api/sites/${slug}/review-queue`).then((r) => { setPages(r.pages); setEntries(r.entries); }).catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug) load(); }, [slug]);

  async function approvePage(id: string) {
    try { await api.post(`/api/sites/${slug}/pages/${id}/publish`); load(); }
    catch (e) { setErr((e as Error).message); }
  }
  async function rejectPage(id: string) {
    const reason = prompt("Reason (optional):") ?? "";
    try { await api.post(`/api/sites/${slug}/pages/${id}/reject`, { reason }); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 07 · Editorial"
        title={<>Review <em>queue</em></>}
        description="Drafts submitted for editorial review. Approve to publish, or reject with a reason."
      />
      {err && <Banner kind="error">{err}</Banner>}

      <Card number="Q1" title={`Pages (${pages.length})`} bodyClassName={pages.length === 0 ? "p-5" : "p-0"}>
        {pages.length === 0 ? (
          <EmptyState title="No pages awaiting review." />
        ) : (
          <StackedList className="px-5">
            {pages.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-4 py-3.5">
                <Link to={`/sites/${slug}/pages/${p.id}`} className="no-underline flex-1 group">
                  <span className="block text-[14px] text-[var(--color-ink)] group-hover:text-[var(--color-accent)] transition-colors">{p.title}</span>
                  <Mono className="text-[11.5px] text-[var(--color-muted)]">/{p.slug}</Mono>
                </Link>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => rejectPage(p.id)}>Reject</Button>
                  <Button size="sm" onClick={() => approvePage(p.id)}>Approve + publish</Button>
                </div>
              </li>
            ))}
          </StackedList>
        )}
      </Card>

      <Card number="Q2" title={`Entries (${entries.length})`} className="mt-6" bodyClassName={entries.length === 0 ? "p-5" : "p-0"}>
        {entries.length === 0 ? (
          <EmptyState title="No entries awaiting review." />
        ) : (
          <StackedList className="px-5">
            {entries.map((e) => (
              <li key={e.id} className="py-3.5">
                <Link to={`/sites/${slug}/collections/${e.collection_id}/entries/${e.id}`} className="no-underline group flex items-baseline gap-2">
                  <span className="text-[14px] text-[var(--color-ink)] group-hover:text-[var(--color-accent)] transition-colors">{e.name}</span>
                  <Mono className="text-[11.5px] text-[var(--color-muted)]">/{e.slug}</Mono>
                </Link>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}
